import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { assertBenchmarkType } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

export const HabitatConnectivityStatsTile = ({
  latestAnalysisFact,
  benchmarks,
}: {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  benchmarks: number[];
}) => {
  const { t } = useTranslation();

  const benchmark = useBenchmarkForFact(benchmarks, latestAnalysisFact?.value, [
    BenchmarkType.very_low,
    BenchmarkType.low,
    BenchmarkType.moderate,
    BenchmarkType.high,
    BenchmarkType.very_high,
  ]);

  if (isNullish(latestAnalysisFact?.value)) {
    return null;
  }

  assertBenchmarkType(benchmark, 'HabitatConnectivityStatsTile', BenchmarkType.unchanged);
  assertBenchmarkType(benchmark, 'HabitatConnectivityStatsTile', BenchmarkType.stable);
  assertBenchmarkType(benchmark, 'HabitatConnectivityStatsTile', BenchmarkType.alerted);

  const latestAnalysisDate = printLatestDate([latestAnalysisFact?.measured_at]);
  const displayValue = `${getDisplayNumber(String(latestAnalysisFact?.value ?? 0))} ${t('global.ui.unit.%')}`;

  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='habitat-connectivity-stats-tile'>
      <Stack direction='row' className='justify-between'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.habitatConnectivity.labels.coreHabitatDistribution')}
          popover={{
            title: t('shared.ncaDetail.details.habitatConnectivity.labels.coreHabitatDistribution'),
            body: t('shared.ncaDetail.details.habitatConnectivity.stats.header.tooltip'),
          }}
        />
        {latestAnalysisDate && (
          <span className='typography-caption text-text-secondary'>
            {t('shared.ncaDetail.details.habitatConnectivity.labels.latestAnalysis', { date: latestAnalysisDate })}
          </span>
        )}
      </Stack>
      {benchmark && (
        <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          <BenchmarkPill benchmarkType={benchmark} />
          <span className='typography-body1Bold'>
            {t('shared.ncaDetail.details.habitatConnectivity.labels.whatDoesThisMean.title')}
          </span>
          <p className='typography-body1'>
            {t(`shared.ncaDetail.details.habitatConnectivity.whatDoesThisMean.body.${benchmark}`, {
              value: displayValue,
            })}
          </p>
        </Stack>
      )}
    </Stack>
  );
};
